import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Drawer as AntdDrawer } from "antd";
import { createContext, useEffect, useMemo, useState } from "react";
import DrawerTitle from "./DrawerTitle";
import "./Drawer.scss";
export const DrawerContext = createContext({
    isExpanded: false
});
/**
 * A drawer component that can be used to display content in two drawers.
 * This is not a HOC for now, and instead expects to be a sibling component
 * to the content that should be displayed outside the drawers.
 * A typical use case is when displaying a list of items,
 * and the user can click on an item to display more details.
 * The second drawer is mainly meant to be used to compare differences between items.
 *
 * In Admin, we use this component to display field relations,
 * where the user can click on a field relation to display the content,
 * and then right-click another field relation to open the second drawer
 * in order to compare the two.
 *
 * When viewing in Storybook, preview not in Docs, but in Default view.
 */
export const TEDrawer = ({ expandWidth = "1404px", onExpand = () => null, defaultExpand = true, firstDrawer = {
    title: "First Drawer",
    content: _jsx(_Fragment, {}),
    drawerOpen: false,
    extra: null,
    setDrawerOpen: () => null
}, secondDrawer = {
    title: "Second Drawer",
    content: _jsx(_Fragment, {}),
    drawerOpen: false,
    extra: null,
    setDrawerOpen: () => null
}, size }) => {
    const [expand, setExpand] = useState(defaultExpand);
    useEffect(() => {
        onExpand(expand);
    }, [expand]);
    useEffect(() => {
        if (firstDrawer.drawerOpen && secondDrawer.drawerOpen) {
            setExpand(false);
        }
    }, [firstDrawer.drawerOpen, secondDrawer.drawerOpen]);
    const _handleExpandDrawer = (drawer) => {
        if (drawer === "firstDrawer") {
            if (secondDrawer.drawerOpen) {
                secondDrawer.setDrawerOpen(false);
                setTimeout(() => setExpand(!expand), 400);
            }
            else {
                setExpand(!expand);
            }
        }
        else {
            if (firstDrawer.drawerOpen) {
                firstDrawer.setDrawerOpen(false);
                setTimeout(() => setExpand(!expand), 450);
            }
            else {
                setExpand(!expand);
            }
        }
    };
    const classNameFirstDrawer = useMemo(() => {
        if (!secondDrawer.drawerOpen)
            return "";
        return firstDrawer.drawerOpen ? "first-drawer-open" : "first-drawer";
    }, [firstDrawer === null || firstDrawer === void 0 ? void 0 : firstDrawer.drawerOpen, secondDrawer === null || secondDrawer === void 0 ? void 0 : secondDrawer.drawerOpen]);
    return (_jsx(DrawerContext.Provider, { value: { isExpanded: expand }, children: _jsxs("div", { children: [_jsx("div", { className: classNameFirstDrawer, children: _jsx(AntdDrawer, { title: _jsx(DrawerTitle, { title: firstDrawer.title, expand: expand, size: size, onExpand: () => _handleExpandDrawer("firstDrawer"), onClose: () => {
                                firstDrawer.setDrawerOpen(false);
                            } }), extra: firstDrawer.extra, width: expand ? expandWidth : 480, getContainer: false, mask: false, closable: false, onClose: () => firstDrawer.setDrawerOpen(false), open: firstDrawer.drawerOpen, children: firstDrawer.content }) }), _jsx(AntdDrawer, { title: _jsx(DrawerTitle, { title: secondDrawer.title, expand: expand, size: size, onExpand: () => _handleExpandDrawer(""), onClose: () => {
                            secondDrawer.setDrawerOpen(false);
                        } }), closable: false, extra: secondDrawer.extra, width: expand ? expandWidth : 480, getContainer: false, mask: false, onClose: () => secondDrawer.setDrawerOpen(false), open: secondDrawer.drawerOpen, children: secondDrawer.content })] }) }));
};
export default TEDrawer;
